import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import type {StateCreator} from 'zustand'

export type StoredCommentsByFile = {
  [filePath: string]: {
    [key: LineKey | FileKey]: boolean
  }
}

export type CommentPersistenceSliceOpts = {
  storedCommentsByFile?: StoredCommentsByFile
}

export type CommentPersistenceSlice = {
  // Diff-lines only needs presence-by-position to decide whether to open comment UI.
  // The actual draft text remains in persisted storage.
  storedCommentsByFile: StoredCommentsByFile
  commentsPersistenceActions: {
    setStoredCommentsForFile: (filePath: string, storedCommentsMap: {[key: LineKey | FileKey]: boolean}) => void
    updateStoredCommentAtPosition: (filePath: string, lineKey: LineKey | FileKey, hasStoredComment: boolean) => void
  }
}

export const createCommentPersistenceSlice =
  ({
    storedCommentsByFile = {},
  }: CommentPersistenceSliceOpts): StateCreator<
    CommentPersistenceSlice,
    [['zustand/devtools', never]],
    [],
    CommentPersistenceSlice
  > =>
  set => ({
    storedCommentsByFile,
    commentsPersistenceActions: {
      setStoredCommentsForFile: (filePath, storedCommentsMap) => {
        set(
          state => {
            const existingStoredCommentsForFile = state.storedCommentsByFile[filePath] ?? {}
            const hasChanges = Object.entries(storedCommentsMap).some(
              ([key, value]) => existingStoredCommentsForFile[key as LineKey | FileKey] !== value,
            )

            if (!hasChanges) return state

            return {
              storedCommentsByFile: {
                ...state.storedCommentsByFile,
                [filePath]: {
                  ...existingStoredCommentsForFile,
                  ...storedCommentsMap,
                },
              },
            }
          },
          undefined,
          'setStoredCommentsForFile',
        )
      },
      updateStoredCommentAtPosition: (filePath, lineKey, hasStoredComment) => {
        // This action always writes an explicit boolean for the target position.
        set(
          state => {
            const existingStoredCommentsForFile = state.storedCommentsByFile[filePath] ?? {}

            if (existingStoredCommentsForFile[lineKey] === hasStoredComment) return state

            return {
              storedCommentsByFile: {
                ...state.storedCommentsByFile,
                [filePath]: {
                  ...existingStoredCommentsForFile,
                  [lineKey]: hasStoredComment,
                },
              },
            }
          },
          undefined,
          'updateStoredCommentAtPosition',
        )
      },
    },
  })
